import 'reflect-metadata';

import { promises as fs } from 'fs';
import path from 'path';
import { Component } from 'react';

import { mediaToolsSlugs } from '../commons/media-tools.config';
import { resolveComponent } from '../components/route-to-component-map';
import { RouteProps } from '../types/route-types';
import { withheldFromBuild } from '../utils/withheld-slugs';

async function getAllFilePaths(postsDirectory: string, urlSet: Set<string>) {
    const paths = await fs.readdir(postsDirectory);
    for (const _path of paths) {
        const fullPath = path.join(postsDirectory, _path);
        const stats = await fs.stat(fullPath);
        if (stats.isDirectory()) {
            getAllFilePaths(fullPath, urlSet);
        } else {
            urlSet.add(fullPath);
        }
    }
}

export async function getStaticPaths() {
    const postsDirectory = path.join(process.cwd(), '/ssr-data/build/')
    const allFilePaths = new Set<string>();
    await getAllFilePaths(postsDirectory, allFilePaths);
    const allowedPaths = [
        new RegExp('.*', 'i'),
    ];
    const disallowedList = [
        /root/i,
        /tools/i,
        /admin/i,
        /^test$/i,
        /faq\//i,
        /product\//i,
        /invalid-path/i,
        /^verify$/i,
        // `/portal` is now an explicit page (pages/portal/index.tsx) per
        // Phase 4 Task 4.4 — it branches on the portalV3Enabled flag and
        // owns its own layout. Excluding the SSR feature variant prevents
        // a "Conflicting paths returned from getStaticPaths" build error.
        /^portal$/i,
        // Exclude media-tools pages (handled by [category].tsx)
        ...mediaToolsSlugs.map(slug => new RegExp(`^${slug}$`, 'i')),
    ];
    const filterFn = (urlPath: string, matchers: RegExp[]) => {
        return matchers.find(p => urlPath.match(p));
    };
    // First extract urlPath from full file path, then filter
    const paths = Array.from(allFilePaths)
        .map(fullPath => {
            const urlPath = fullPath.replace(postsDirectory, '').replace('.json', '');
            return { fullPath, urlPath };
        })
        .filter(({ urlPath }) => filterFn(urlPath, allowedPaths))
        .filter(({ urlPath }) => !filterFn(urlPath, disallowedList))
        // Withheld features (utils/withheld-slugs.ts, B1 2026-10-04): emitted only by `next dev`
        // or XC_INCLUDE_WITHHELD=1, however the json came to sit in ssr-data/build/.
        .filter(({ urlPath }) => !withheldFromBuild(urlPath))
        .map(({ urlPath }) => {
            return { params: { feature: urlPath } };
        });
    console.log(`Number of feature paths: ${paths.length}`);
    return {
        paths:
            paths,
        // [
        //     { params: { feature: 'compress-mp4' } } // See the "paths" section below
        // ],
        fallback: false // See the "fallback" section below
    };
}

export async function getStaticProps({ params }) {
    const props = `ssr-data/build/${params.feature}.json`;
    // console.log(`getStaticProps called for ${params.feature}`);
    const data = await fs.readFile(props);
    const jObj = JSON.parse(data.toString());
    // Pass post data to the page via props
    return { props: jObj };
}

export default class FeaturePage extends Component<RouteProps> {

    constructor(props) {
        super(props);
    }

    render() {
        const Comp = resolveComponent(this.props.path);
        return <Comp key={this.props.path} {...this.props}></Comp>;
    }
}
